
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Zap, 
  Shield, 
  Globe, 
  BarChart3, 
  Users, 
  Cpu, 
  Download, 
  FileText,
  Palette,
  Clock,
  Lock,
  Headphones,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { useState } from "react";

const EnterpriseFeatures = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const features = [
    {
      icon: Zap,
      title: "Lightning Fast Generation",
      description: "Generate up to 1000 images in under 60 seconds with our optimized AI infrastructure."
    },
    {
      icon: Shield,
      title: "Enterprise Security",
      description: "SOC 2 Type II certified with end-to-end encryption and secure API access."
    },
    {
      icon: Globe,
      title: "Global CDN",
      description: "Worldwide content delivery network ensures fast image generation from anywhere."
    },
    {
      icon: BarChart3,
      title: "Advanced Analytics",
      description: "Comprehensive dashboards and reporting for generation metrics and usage tracking."
    },
    {
      icon: Users,
      title: "Team Collaboration",
      description: "Multi-user workspaces with role-based permissions and shared project management."
    },
    {
      icon: Cpu,
      title: "API Integration",
      description: "RESTful APIs and webhooks for seamless integration with your existing workflows."
    },
    {
      icon: Download,
      title: "Bulk Export",
      description: "Export thousands of images in multiple formats with organized folder structures."
    },
    {
      icon: FileText,
      title: "CSV & JSON Import",
      description: "Import prompts from spreadsheets or structured data for automated generation."
    },
    {
      icon: Palette,
      title: "Custom Styles",
      description: "Apply consistent branding and styling across all generated images."
    },
    {
      icon: Clock,
      title: "Scheduled Generation",
      description: "Automate image creation with scheduled jobs and recurring campaigns."
    },
    {
      icon: Lock,
      title: "Private Cloud",
      description: "Dedicated instances and private cloud deployment options for enterprises."
    },
    {
      icon: Headphones,
      title: "24/7 Support",
      description: "Dedicated customer success team with priority support and SLA guarantees."
    }
  ];

  // Mobile shows 2 items, tablet shows 4, desktop shows 6
  const getItemsPerSlide = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth < 640) return 2; // mobile
      if (window.innerWidth < 1024) return 4; // tablet
    }
    return 6; // desktop
  };

  const [itemsPerSlide, setItemsPerSlide] = useState(getItemsPerSlide());
  const totalSlides = Math.ceil(features.length / itemsPerSlide);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const getCurrentFeatures = () => {
    const start = currentSlide * itemsPerSlide;
    return features.slice(start, start + itemsPerSlide);
  };

  return (
    <section id="features" className="py-16 xs:py-20 sm:py-24 lg:py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Background patterns matching hero */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:12px_12px] xs:bg-[size:16px_16px] sm:bg-[size:20px_20px] md:bg-[size:24px_24px]"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-transparent to-purple-600/10"></div>
        <div className="absolute top-1/4 left-0 w-32 h-32 xs:w-48 xs:h-48 sm:w-64 sm:h-64 lg:w-96 lg:h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-0 w-32 h-32 xs:w-48 xs:h-48 sm:w-64 sm:h-64 lg:w-96 lg:h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
      </div>

      <div className="container mx-auto px-4 xs:px-6 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12 xs:mb-16 sm:mb-20 md:mb-12">
          <h2 className="text-3xl xs:text-4xl sm:text-5xl md:text-4xl lg:text-5xl font-bold text-white mb-4 xs:mb-6 sm:mb-8 md:mb-4 leading-tight">
            Enterprise-Grade
            <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 bg-clip-text text-transparent block mt-2 xs:mt-3 sm:mt-4 md:mt-2"> Features</span>
          </h2>
          <p className="text-base xs:text-lg sm:text-xl md:text-lg lg:text-lg text-gray-300 max-w-sm xs:max-w-lg sm:max-w-2xl lg:max-w-3xl mx-auto leading-relaxed px-3 xs:px-4 sm:px-0">
            Everything you need to scale your image generation workflows, from small teams to global enterprises.
          </p>
        </div>
        
        {/* Slider Container - Enhanced mobile layout with reduced height */}
        <div className="relative">
          <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 xs:gap-6 sm:gap-8 lg:gap-8">
            {getCurrentFeatures().map((feature, index) => (
              <Card key={currentSlide * itemsPerSlide + index} className="bg-white/10 backdrop-blur-md border-white/20 hover:bg-white/15 transition-all duration-300 group hover:shadow-2xl hover:shadow-blue-500/20 touch-manipulation active:scale-95 h-full">
                <CardContent className="p-4 xs:p-5 sm:p-6 h-full flex flex-col">
                  <div className="w-10 h-10 xs:w-12 xs:h-12 sm:w-12 sm:h-12 bg-gradient-to-r from-blue-600/30 to-purple-600/30 rounded-xl flex items-center justify-center mb-4 xs:mb-5 sm:mb-4 backdrop-blur-sm border border-white/20 shadow-lg">
                    <feature.icon className="w-5 h-5 xs:w-6 xs:h-6 sm:w-6 sm:h-6 text-blue-400 group-hover:text-purple-400 transition-colors" />
                  </div>
                  <h3 className="text-base xs:text-lg sm:text-lg font-semibold text-white mb-2 xs:mb-3 sm:mb-2 leading-tight">{feature.title}</h3>
                  <p className="text-gray-300 leading-relaxed text-xs xs:text-sm sm:text-xs flex-grow">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Navigation Buttons - Enhanced mobile touch targets */}
          <div className="flex justify-center items-center mt-8 xs:mt-10 sm:mt-8 space-x-6 xs:space-x-8 sm:space-x-4">
            <Button
              onClick={prevSlide}
              variant="outline"
              size="sm"
              className="bg-white/10 border-white/20 text-white hover:bg-white/20 w-12 h-12 xs:w-14 xs:h-14 sm:w-auto sm:h-auto sm:px-4 sm:py-2 touch-manipulation active:scale-95 transition-transform"
              disabled={totalSlides <= 1}
            >
              <ChevronLeft className="w-5 h-5 xs:w-6 xs:h-6 sm:w-4 sm:h-4" />
            </Button>
            
            <div className="flex space-x-2 xs:space-x-3 sm:space-x-2">
              {Array.from({ length: totalSlides }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 xs:w-4 xs:h-4 sm:w-2 sm:h-2 rounded-full transition-colors touch-manipulation ${
                    index === currentSlide ? 'bg-blue-400' : 'bg-white/30'
                  }`}
                />
              ))}
            </div>
            
            <Button
              onClick={nextSlide}
              variant="outline"
              size="sm"
              className="bg-white/10 border-white/20 text-white hover:bg-white/20 w-12 h-12 xs:w-14 xs:h-14 sm:w-auto sm:h-auto sm:px-4 sm:py-2 touch-manipulation active:scale-95 transition-transform"
              disabled={totalSlides <= 1}
            >
              <ChevronRight className="w-5 h-5 xs:w-6 xs:h-6 sm:w-4 sm:h-4" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EnterpriseFeatures;
